import { useEffect, useState } from "react";
import { api, dispDate, todayYMD } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

export default function AdminLeave() {
  const [users, setUsers] = useState([]);
  const [list, setList] = useState([]);
  const [userId, setUserId] = useState("");
  const [date, setDate] = useState(todayYMD());
  const [type, setType] = useState("izin");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () => api.get("/leaves").then((r) => setList(r.data));
  useEffect(() => {
    Promise.all([
      api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "pegawai"))),
      refresh(),
    ]).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!userId) return toast.error("Pilih pegawai");
    setBusy(true);
    try {
      await api.post("/leaves", { user_id: userId, leave_date: date, type, note });
      toast.success("Tersimpan");
      setNote("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const del = async (l) => {
    if (!window.confirm("Hapus data izin ini?")) return;
    try {
      await api.delete(`/leaves/${l.id}`);
      toast.success("Dihapus");
      refresh();
    } catch { toast.error("Gagal"); }
  };

  const umap = Object.fromEntries(users.map((u) => [u.id, u]));

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-5xl mx-auto">
      <p className="overline">Manajemen</p>
      <h1 className="font-display text-3xl font-bold tracking-tight">Izin / Sakit / Cuti</h1>

      {loading ? <PageLoader label="Memuat data izin..." /> : (<>
      <Card className="mt-6 p-6 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Input</p>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Pegawai</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="mt-1" data-testid="leave-user"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tanggal</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="leave-date" />
          </div>
          <div>
            <Label>Jenis</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1" data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="izin">Izin</SelectItem>
                <SelectItem value="sakit">Sakit</SelectItem>
                <SelectItem value="cuti">Cuti</SelectItem>
                <SelectItem value="lainnya">Lainnya</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <Label>Catatan</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" data-testid="leave-note" />
          </div>
          <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90 md:col-span-1" data-testid="btn-save-leave">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" />Simpan</>}
          </Button>
        </form>
      </Card>

      <div className="mt-6 space-y-2">
        {list.map((l) => (
          <Card key={l.id} className="p-3 border border-zinc-200 shadow-none flex justify-between items-center" data-testid={`leave-${l.id}`}>
            <div>
              <p className="font-semibold">
                {umap[l.user_id]?.name || l.user_id}
                <Badge variant="outline" className="ml-2 uppercase">{l.type}</Badge>
              </p>
              <p className="text-xs text-zinc-500">{dispDate(l.leave_date)} · {l.note}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => del(l)} data-testid={`btn-del-leave-${l.id}`}>
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          </Card>
        ))}
      </div>
      </>)}
    </div>
  );
}
